import { Dispatch, SetStateAction, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  UserKeys,
  deleteSharedContacts,
  deleteSharedContactsUserPortal,
} from '../../../api/users-api';
import { handleErrors } from '../../../utils/errors';
import { Page, SharedContactModel } from '../../../../types';
import SharedDialog from '../../../shared/shared-dialog';
import ChangeLogDialog from '../../../shared/change-log-dialog';
import { useStore } from '../../state/zustand-store';
import { useAnalytics } from '../../../shared/analytics/create-events';
import * as analytics from '../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../shared/analytics/module-list';
import {
  DirectoryRoles,
  isAbleToSeeUIElement,
} from '../../../utils/roles/client-roles';
import { useUserSettings } from '../../../utils/use-user-settings';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

interface DeleteContactsDialogProps {
  open: boolean;
  handleClose: () => void;
  selectedRows: string[];
  setSelectedRows: Dispatch<SetStateAction<string[]>>;
  contactList: SharedContactModel[];
  userView?: boolean;
  page: number;
  pageSize: number;
  searchTerm: string;
}

export default function DeleteContactsDialog({
  open,
  handleClose,
  selectedRows,
  setSelectedRows,
  contactList,
  page,
  pageSize,
  searchTerm,
  userView,
}: DeleteContactsDialogProps) {
  const { createEvent } = useAnalytics();
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');

  const { primaryDomain, changeLogRoles } = useUserSettings();

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      deleteSharedContacts(
        primaryDomain,
        selectedContacts,
        'User',
        changeLogMessage
      ),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'Contact(s) deleted successfully!',
      });

      const newList = contactList.filter(
        (contact) => !selectedRows.includes(contact.id)
      );
      const pageObject: Page<SharedContactModel[]> | undefined =
        queryClient.getQueryData([
          UserKeys.SharedContacts,
          page,
          pageSize,
          searchTerm,
        ]);
      queryClient.setQueryData(
        [UserKeys.SharedContacts, page, pageSize, searchTerm],
        {
          ...pageObject,
          results: newList,
        }
      );
      queryClient.invalidateQueries({
        queryKey: [UserKeys.SharedContacts, page, pageSize, ''],
      });
      setSelectedRows([]);
      setChangeLogMessage('');
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue deleting the selected shared contacts. Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(analytics.directory.shared_contacts.delete_shared_contacts, {
        module: ModuleList.modules.directory.name,
        section: ModuleList.modules.directory.sections.sharedContacts,
      });
      setOpenChangeLogDialog(false);
    },
  });

  const { mutate: userPortalMutate, isPending: userPortalPending } =
    useMutation({
      mutationFn: () =>
        deleteSharedContactsUserPortal(primaryDomain, selectedContacts, 'User'),
      onSuccess: () => {
        setSnackbar({
          open: true,
          severity: 'success',
          message: 'Contact(s) deleted successfully!',
        });

        const newList = contactList.filter(
          (contact) => !selectedRows.includes(contact.id)
        );
        const pageObject: Page<SharedContactModel[]> | undefined =
          queryClient.getQueryData([
            UserKeys.SharedContacts,
            page,
            pageSize,
            searchTerm,
          ]);
        queryClient.setQueryData(
          [UserKeys.SharedContacts, page, pageSize, searchTerm],
          {
            ...pageObject,
            results: newList,
          }
        );
        queryClient.invalidateQueries({
          queryKey: [UserKeys.SharedContacts, page, pageSize, ''],
        });
        setSelectedRows([]);
        handleClose();
      },
      onError: (error) => {
        handleErrors({
          error,
          snackbarMessage:
            'There was an issue deleting the selected shared contacts. Please refresh and try again.',
        });
      },
      onSettled: () => {
        createEvent(analytics.userView.shared_contacts.delete_shared_contacts, {
          module: ModuleList.modules.userView.name,
          section: ModuleList.modules.userView.sections.sharedContacts,
        });
      },
    });

  function handleSubmit() {
    if (
      !userView &&
      isAbleToSeeUIElement(
        changeLogRoles,
        DirectoryRoles.DeleteSharedContacts.value
      )
    ) {
      setOpenChangeLogDialog(true);
    } else if (userView) {
      userPortalMutate();
    } else {
      mutate();
    }
  }

  const selectedContacts = contactList.filter((contact) =>
    selectedRows.includes(contact.id)
  );

  selectedContacts.forEach((contact) => {
    contact.orgs?.forEach((org) => {
      delete org.orgVal?.orgString;
    });
  });

  const firstContact =
    selectedContacts.length > 0
      ? selectedContacts[0].emails?.find((email) => email.rel === 'Primary')
          ?.val || selectedContacts[0].id
      : 'unknown';

  const isMultiple = selectedContacts.length > 1;

  return (
    <SharedDialog
      open={open}
      handleClose={handleClose}
      title={isMultiple ? 'Delete Contacts' : 'Delete Contact'}
      dialogContent={
        <Box>
          <Typography>
            {isMultiple ? (
              <>
                Delete <strong>{selectedRows.length}</strong> shared contacts
                from <strong>{primaryDomain}'s</strong> shared contacts?
              </>
            ) : (
              <>
                Delete <strong>{firstContact}</strong> from{' '}
                <strong>{primaryDomain}'s</strong> shared contacts?
              </>
            )}
          </Typography>
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isPending}
          />
        </Box>
      }
      primaryButton={{
        title: isMultiple ? 'Delete Contacts' : 'Delete Contact',
        color: 'error',
        action: handleSubmit,
        loading: isPending || userPortalPending,
        disabled: isPending || userPortalPending,
      }}
    />
  );
}
